import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check, X as XMark, Server, Globe, Users, Shield, Zap, LayoutGrid, BarChart3, MessageSquare, Megaphone, Sparkles } from 'lucide-react';
import SEO from '@/components/SEO';
import Icon from '@/components/Icon';
import BrandIcon from '@/components/BrandIcon';
import BrandMarquee from '@/components/BrandMarquee';
import { workspaceFeatures, channels, site, useCases, serviceQuality, customerStats, featureHighlights, scalability, comparison } from '@/data/content';

interface FeatureItem {
  title: string;
  description: string;
  icon: string;
}

function FeatureCard({ feature, featured = false }: { feature: FeatureItem; featured?: boolean }) {
  return (
    <div className={`group relative overflow-hidden rounded-3xl border bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
      featured 
        ? 'border-brand-200 p-8 shadow-lg shadow-brand-100/50' 
        : 'border-slate-100 p-6 shadow-sm shadow-slate-200/40'
    }`}>
      {featured && (
        <div className="absolute right-0 top-0 h-24 w-24 translate-x-8 -translate-y-8 rounded-full bg-gradient-to-br from-brand-100 to-brand-50 opacity-50" />
      )}
      <div className={`mb-5 flex items-center justify-center rounded-2xl bg-gradient-to-br shadow-inner transition ${
        featured 
          ? 'h-16 w-16 from-brand-600 to-brand-400 text-white' 
          : 'h-14 w-14 from-slate-50 to-slate-100 text-brand-700 group-hover:from-brand-600 group-hover:to-brand-700 group-hover:text-white'
      }`}>
        <Icon name={feature.icon} className={featured ? 'h-8 w-8' : 'h-7 w-7'} />
      </div>
      <h3 className={`font-extrabold text-slate-900 ${featured ? 'text-xl' : 'text-lg'}`}>{feature.title}</h3>
      <p className={`mt-3 leading-relaxed text-slate-600 ${featured ? 'text-base' : 'text-sm'}`}>{feature.description}</p>
    </div>
  );
}

export default function Features() {
  const allFeatures = useMemo(
    () => workspaceFeatures.flatMap((cat) => cat.features),
    []
  );
  const categories = useMemo(
    () => workspaceFeatures.map((cat) => cat.category),
    []
  );
  const [active, setActive] = useState('All');
  const filtered =
    active === 'All'
      ? allFeatures
      : workspaceFeatures.find((cat) => cat.category === active)?.features ?? [];
  const activeCategory = workspaceFeatures.find((cat) => cat.category === active);

  const featureFaqs = [
    {
      q: 'What can ZuriHub do?',
      a: 'ZuriHub unifies live chat, tickets, knowledge base, omnichannel messaging, AI replies, voice, and analytics in one workspace.',
    },
    {
      q: 'Does ZuriHub work for small teams?',
      a: 'Yes. The free plan supports one agent, and Pro scales at $25 per agent per month with all channels and AI included.',
    },
    {
      q: 'Which channels are supported?',
      a: 'WhatsApp, Instagram, Telegram, Messenger, X, Gmail, live chat, and email all feed into the same shared inbox.',
    },
    {
      q: 'Is Zyra AI included?',
      a: 'Yes. Zyra drafts replies, summarizes threads, and coaches agents on every plan that includes AI.',
    },
  ];

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'ZuriHub Features',
      url: `${site.baseUrl}/features`,
      description: `${site.name} features include conversations, tickets, AI, knowledge base, billing, analytics, and more.`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: allFeatures.map((f, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: f.title,
        description: f.description,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: featureFaqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  function compareCell(value: boolean | string, highlight = false) {
    if (typeof value === 'string') {
      const isPrice = value.startsWith('$');
      return <span className={`${isPrice ? 'font-bold' : 'font-medium'} ${highlight ? 'text-brand-700' : 'text-slate-700'}`}>{value}</span>;
    }
    if (value === true) return <Check className="mx-auto h-5 w-5 text-green-600" />;
    if (value === false) return <XMark className="mx-auto h-5 w-5 text-slate-300" />;
    return null;
  }

  function compareBadge(value: string) {
    if (value === 'partial') return <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-700">Partial</span>;
    if (value === 'add-on') return <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-slate-700">Add-on</span>;
    return null;
  }

  return (
    <>
      <SEO
        title="Features"
        description="Explore every feature of Zuri Hub: conversations, tickets, AI, knowledge base, billing, analytics, and more."
        path="/features"
        jsonLd={jsonLd}
      >
        <meta
          name="keywords"
          content="ZuriHub features, AI customer service features, omnichannel inbox, live chat, tickets, knowledge base, Zyra AI, analytics, CRM"
        />
      </SEO>

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Zap className="h-4 w-4" /> All-in-one CX platform
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                One workspace.<br />
                <span className="gradient-text">Infinite possibilities.</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Replace your chat widget, help desk, and AI tools with ZuriHub. Serve customers across every channel from a single, powerful workspace.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 shadow-xl shadow-white/10 transition hover:bg-slate-100"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <Link
                  to="/book-a-demo"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Book a Demo <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative rounded-3xl border border-slate-700 bg-slate-900/60 p-2 backdrop-blur">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/SI-Diagram-Simple-Conversation-API-1.webp"
                  alt="ZuriHub conversation API diagram"
                  className="rounded-2xl w-full h-auto"
                  width="800"
                  height="600"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 py-4 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {['All', ...categories].map((cat) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-bold transition ${
                  active === cat
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'border border-slate-200 bg-white text-slate-700 hover:border-brand-300 hover:text-brand-700'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-label="Feature directory">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {active === 'All' ? (
            <>
              <div className="mb-10 text-center">
                <h2 className="text-3xl font-extrabold text-slate-900 sm:text-4xl">Powerful features, one workspace</h2>
                <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600">
                  Everything you need to deliver exceptional customer experiences, without the complexity of multiple tools.
                </p>
              </div>
              
              <div className="mb-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.slice(0, 3).map((feature) => (
                  <FeatureCard key={feature.title} feature={feature} featured />
                ))}
              </div>
              
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.slice(3).map((feature) => (
                  <FeatureCard key={feature.title} feature={feature} />
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="mb-10 text-center">
                <h2 className="text-3xl font-extrabold text-slate-900 sm:text-4xl">{active}</h2>
                <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600">{activeCategory?.summary}</p>
              </div>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((feature) => (
                  <FeatureCard key={feature.title} feature={feature} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="bg-gradient-to-br from-slate-900 to-slate-950 py-24 text-white" aria-labelledby="use-cases-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300">
              <Megaphone className="h-4 w-4" /> Real results
            </span>
            <h2 id="use-cases-heading" className="mt-6 text-3xl font-extrabold sm:text-4xl">Built for real business impact</h2>
            <p className="mt-4 text-lg text-slate-300">How teams use ZuriHub to close faster, serve better, and scale smarter.</p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {useCases.map((uc) => (
              <div
                key={uc.title}
                className="group relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/60 p-8 backdrop-blur transition-all hover:-translate-y-2 hover:bg-slate-800 hover:shadow-xl"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-brand-600/10 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                <div className="relative mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-600 to-brand-400 text-white shadow-lg shadow-brand-500/30">
                  <Icon name={uc.icon} className="h-7 w-7" />
                </div>
                <h3 className="relative text-lg font-extrabold text-white">{uc.title}</h3>
                <p className="relative mt-3 text-sm leading-relaxed text-slate-400">{uc.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="quality-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-16 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand-700">
                <Shield className="h-4 w-4" /> Enterprise-grade
              </span>
              <h2 id="quality-heading" className="mt-6 text-3xl font-extrabold text-slate-900 sm:text-4xl">Built for service quality</h2>
              <p className="mt-4 text-lg text-slate-600">Reliable, fast, and secure by default. Your customers deserve the best.</p>
              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                {serviceQuality.map((s) => (
                  <div
                    key={s.label}
                    className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                  >
                    <div className="text-3xl font-extrabold text-brand-600 sm:text-4xl">{s.metric}</div>
                    <div className="mt-2 font-bold text-slate-900">{s.label}</div>
                    <p className="mt-2 text-sm text-slate-600">{s.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-8 shadow-sm">
                <h3 className="text-2xl font-extrabold text-slate-900">Trusted by growing teams</h3>
                <p className="mt-4 text-slate-600">From startups to scale-ups, teams trust ZuriHub with their most important conversations.</p>
                <div className="mt-8 grid gap-6 sm:grid-cols-2">
                  {customerStats.map((s) => (
                    <div
                      key={s.label}
                      className="rounded-2xl border border-slate-100 bg-white p-6 text-center shadow-sm"
                    >
                      <div className="text-3xl font-extrabold text-slate-900 sm:text-4xl">{s.value}</div>
                      <div className="mt-2 font-bold text-slate-900">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="highlights-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand-700">
              <LayoutGrid className="h-4 w-4" /> Scalable foundation
            </span>
            <h2 id="highlights-heading" className="mt-6 text-3xl font-extrabold text-slate-900 sm:text-4xl">Built to grow with you</h2>
            <p className="mt-4 text-lg text-slate-600">From your first customer to your millionth, the same platform scales without limits.</p>
          </div>
          <div className="mt-14 grid gap-8 lg:grid-cols-2">
            {featureHighlights.map((h) => (
              <div
                key={h.id}
                className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="absolute right-0 top-0 h-32 w-32 translate-x-16 -translate-y-16 rounded-full bg-gradient-to-br from-brand-100 to-brand-50 opacity-50 transition-opacity group-hover:opacity-100" />
                <div className="relative mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-600 to-brand-400 text-white shadow-lg shadow-brand-500/30">
                  <Icon name={h.icon} className="h-8 w-8" />
                </div>
                <h3 className="relative text-2xl font-extrabold text-slate-900">{h.title}</h3>
                <p className="relative mt-3 text-slate-600">{h.summary}</p>
                <ul className="relative mt-6 space-y-3">
                  {h.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm font-semibold text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="scale-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="scale-heading" className="section-title">How we scale with you</h2>
            <p className="section-subtitle mx-auto">From your first agent to your thousandth on the same platform.</p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-3">
            {scalability.tiers.map((tier, index) => (
              <div
                key={tier.plan}
                className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-2 hover:shadow-xl"
              >
                <div className="text-sm font-bold text-slate-500">{tier.agents} agents</div>
                <h3 className="mt-1 text-2xl font-extrabold text-slate-900">{tier.plan}</h3>
                <p className="mt-4 text-slate-600">{tier.capabilities}</p>
                <div className="mt-6 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className={`h-1.5 rounded-full ${['bg-slate-400','bg-brand-600','bg-slate-900'][index]}`} style={{ width: `${(index + 1) * 33}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: 'Server', title: 'Auto-scaling cloud', text: 'Zuri Cloud scales compute and storage as your volume grows.' },
              { icon: 'Globe', title: 'Global edge delivery', text: 'Conversations are routed through the fastest nodes worldwide.' },
              { icon: 'Users', title: 'Unlimited contacts', text: 'No contact limits, no hidden fees, no penalties for growth.' },
              { icon: 'Shield', title: 'Enterprise controls', text: 'SSO, audit logs, and custom SLAs when you need them.' },
            ].map((item, i) => {
              const I = [Server, Globe, Users, Shield][i];
              return (
                <div
                  key={item.title}
                  className="rounded-2xl border border-slate-100 bg-slate-50 p-6 shadow-sm transition hover:-translate-y-2 hover:shadow-xl"
                >
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-brand-700 shadow-sm">
                    <I className="h-6 w-6" />
                  </div>
                  <h4 className="font-bold text-slate-900">{item.title}</h4>
                  <p className="mt-2 text-sm text-slate-600">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="compare-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand-700">
              <BarChart3 className="h-4 w-4" /> Better value
            </span>
            <h2 id="compare-heading" className="mt-6 text-3xl font-extrabold text-slate-900 sm:text-4xl">Why teams choose ZuriHub</h2>
            <p className="mt-4 text-lg text-slate-600">More features, better AI, simpler pricing. See how we compare.</p>
          </div>
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left">
                    <th className="px-6 py-4 font-bold text-slate-700">Feature</th>
                    <th className="px-6 py-4 text-center font-bold text-brand-700">ZuriHub</th>
                    <th className="px-6 py-4 text-center font-bold text-slate-500">Intercom</th>
                    <th className="px-6 py-4 text-center font-bold text-slate-500">Zendesk</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.rows.map((row) => (
                    <tr key={row.feature} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                      <td className="px-6 py-4 font-medium text-slate-900">{row.feature}</td>
                      <td className="px-6 py-4 text-center bg-brand-50/30">{compareCell(row.zurihub, true)}</td>
                      <td className="px-6 py-4 text-center">
                        {typeof row.intercom === 'string' && ['partial', 'add-on'].includes(row.intercom) ? compareBadge(row.intercom) : compareCell(row.intercom)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {typeof row.zendesk === 'string' && ['partial', 'add-on'].includes(row.zendesk) ? compareBadge(row.zendesk) : compareCell(row.zendesk)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-slate-900 to-slate-950 py-24 text-white" aria-labelledby="channels-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300">
              <MessageSquare className="h-4 w-4" /> True omnichannel
            </span>
            <h2 id="channels-heading" className="mt-6 text-3xl font-extrabold text-white sm:text-4xl">Every channel, one inbox</h2>
            <p className="mt-4 text-lg text-slate-300">
              Real integrations. Real-time sync. No switching between apps.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {channels.map((channel) => (
              <div
                key={channel.name}
                className="group flex items-center gap-4 rounded-2xl border border-slate-800 bg-slate-900/60 p-4 backdrop-blur transition-all hover:-translate-y-1 hover:border-brand-500/50 hover:bg-slate-800"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-800 shadow-sm transition group-hover:scale-110 group-hover:bg-brand-600">
                  <BrandIcon name={channel.brand} className="h-7 w-7" />
                </div>
                <span className="text-base font-bold text-white">{channel.name}</span>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <p className="text-sm text-slate-400">
              <span className="font-semibold text-white">Connect in minutes</span> — No technical setup required
            </p>
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="features-faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand-700">
              <Sparkles className="h-4 w-4" /> Quick answers
            </span>
            <h2 id="features-faq-heading" className="mt-6 text-3xl font-extrabold text-slate-900 sm:text-4xl">
              Common questions
            </h2>
            <p className="mt-4 text-lg text-slate-600">Everything you need to know about ZuriHub features.</p>
          </div>
          <div className="mt-12 space-y-4">
            {featureFaqs.map((faq) => (
              <details
                key={faq.q}
                className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:shadow-md"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between text-lg font-bold text-slate-900">
                  {faq.q}
                  <ArrowRight className="h-5 w-5 text-slate-400 transition group-open:rotate-90" />
                </summary>
                <p className="mt-4 leading-relaxed text-slate-600">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-gradient-to-br from-brand-600 to-brand-700 py-24 text-white" aria-label="Get started with features">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-white/10 to-transparent" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Ready to transform your customer experience?</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Join teams that close faster, serve better, and scale smarter with ZuriHub.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-brand-700 shadow-xl transition hover:bg-slate-100"
            >
              Start Free Trial <ArrowUpRight className="h-4 w-4" />
            </a>
            <Link
              to="/book-a-demo"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/10 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:bg-white/20"
            >
              Book a Demo <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <p className="mt-6 text-sm text-brand-200">No credit card required. Free forever plan for 1 agent.</p>
        </div>
      </section>
    </>
  );
}
